import {
  canChangeScheduledTime,
  FULL_SONG_VIDEO_THEME_IDS,
  publishOfAssetType,
  type SerializedAssetPublish,
} from 'fym-shared/src/music24/oneOffPublish';
import { renderPartInProgress } from 'fym-shared/src/music24/fullVideoPlan';
import type { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { sourceOfAsset } from '../../promote/addedAssets/addedAssetFacts';
import type { KitSlotView } from '../../promote/releases/kit/kitSlots';
import type { ServiceExtraActionLook } from '../extras/serviceExtraTypes';

/**
 * "YouTube extras" (design service-extras a1, a2): the full-song videos of the song (music video,
 * lyric video, visualizer) and their one-off publish to YouTube (`POST .../asset-publishes`, the
 * PublishDialog). Manual only: owner decision 2026-10-18, there is no YouTube auto upload, so the
 * box has no switch. The existing "Publish to YouTube when ready" Studio switch makes a `waiting`
 * record, and the box shows it. Pure, so it is tested without React.
 */

export type FullSongVideoThemeId = (typeof FULL_SONG_VIDEO_THEME_IDS)[number];

/** The order of the items (design a0): music video, lyric video, visualizer. */
export const YOUTUBE_EXTRA_ORDER: readonly FullSongVideoThemeId[] = ['musicVideo', 'lyricVideo', 'visualizer'];

/** The YouTube channel of the artist (the Promote channel of the one YouTube sign-in). */
export type YouTubeLogin = 'none' | 'connected' | 'reconnect';

/** The login of the YouTube Promote channel (`GET promo/channels`). */
export function youtubeLoginOf(
  channel: { status: string; connection: unknown; needsReconnect: boolean } | undefined
): YouTubeLogin {
  if (channel === undefined || channel.connection === null) {
    return 'none';
  }
  if (channel.needsReconnect) {
    return 'reconnect';
  }
  return channel.status === 'connected' ? 'connected' : 'reconnect';
}

export type YouTubeExtraState =
  /** No YouTube channel and nothing on YouTube: one "Full videos" item with Connect YouTube. */
  | 'notConnected'
  /** No full video of the song at all: one "Music video" item with Make it. */
  | 'noVideo'
  /** The video renders; no record. */
  | 'making'
  /** The video renders, and a `waiting` record uploads it when it is ready (the Studio switch). */
  | 'waiting'
  /** The render failed and no file of the type exists. */
  | 'renderFailed'
  /** A ready file with no live record. */
  | 'ready'
  /** A ready file, and the YouTube sign-in is gone. */
  | 'readyNotConnected'
  | 'uploading'
  | 'scheduled'
  | 'live'
  /** The version was added from a YouTube link: it is on YouTube already. */
  | 'linked'
  | 'failed'
  | 'reconnect'
  | 'removed';

export type YouTubeExtraChip =
  | { kind: 'rendering'; progress: number }
  | { kind: 'goesUp' }
  | { kind: 'uploading' }
  | { kind: 'publicAt'; at: string | null }
  | { kind: 'onYouTube' }
  | { kind: 'private' }
  | { kind: 'failed' }
  | { kind: 'reconnect' }
  | { kind: 'removed' }
  | { kind: 'notMade' };

export type YouTubeExtraAction =
  | 'connect'
  | 'reconnect'
  | 'makeIt'
  | 'upload'
  | 'replace'
  | 'retry'
  | 'uploadAgain'
  | 'status'
  | 'changeTime'
  | 'open';

export interface YouTubeExtraActionView {
  action: YouTubeExtraAction;
  look: ServiceExtraActionLook;
}

export interface YouTubeExtraItemView {
  /** `connect`, `noVideo`, or the asset type. Also the test id `service-extra-youtube-<id>`. */
  id: 'connect' | 'noVideo' | FullSongVideoThemeId;
  /** The asset type of the item; null for the "Full videos" item. */
  themeId: FullSongVideoThemeId | null;
  state: YouTubeExtraState;
  chips: YouTubeExtraChip[];
  actions: YouTubeExtraActionView[];
  /** The ready file: Upload, Replace and Try again send it. */
  readyAsset: SerializedKitAsset | null;
  /** The YouTube record that the item shows (the newest of its type, a live one first). */
  record: SerializedAssetPublish | null;
  /** A newer ready revision than the one on YouTube: "Revision 3 is ready." */
  newerRevision: number | null;
  /** 0..100 of a video that is made now, for the bar; null for none. */
  progress: number | null;
  /** "Rendering part 4 of 8": the part in progress and the parts; null for a short render. */
  part: { current: number; total: number } | null;
  /** An action of the artist waits: the box opens by itself (design a0 fold rule). */
  waits: boolean;
}

export interface YouTubeExtrasInput {
  /** The Kit slots of the song (`buildKitSlots`); only the full-song video slots are read. */
  slots: readonly KitSlotView[];
  /** `GET asset-publishes?catalogItemUuid` (newest first). */
  publishes: readonly SerializedAssetPublish[];
  catalogItemUuid: string;
  login: YouTubeLogin;
  /** The demo artist: the box with no button. */
  readOnly: boolean;
  now: Date;
}

const LINK = 'link' as const;
const PRIMARY = 'primary' as const;

function partOf(asset: SerializedKitAsset | null): { current: number; total: number } | null {
  const total = asset?.renderPartsTotal ?? null;
  if (asset === null || total === null || total <= 1) {
    return null;
  }
  const current = renderPartInProgress(asset.renderPartsDone, total);
  return current === null ? null : { current, total };
}

function emptyItem(themeId: FullSongVideoThemeId): Omit<YouTubeExtraItemView, 'state' | 'actions' | 'waits'> {
  return {
    id: themeId,
    themeId,
    chips: [],
    readyAsset: null,
    record: null,
    newerRevision: null,
    progress: null,
    part: null,
  };
}

/** The item of one asset type, or null when the song has nothing of that type. */
function typeItem(
  input: YouTubeExtrasInput,
  slot: KitSlotView,
  themeId: FullSongVideoThemeId
): YouTubeExtraItemView | null {
  const ready = slot.readyAsset;
  const working = slot.workingAsset;
  const variant = (ready ?? working ?? slot.failedAsset)?.variant ?? null;
  const record =
    variant === null
      ? null
      : publishOfAssetType(input.publishes, { catalogItemUuid: input.catalogItemUuid, themeId, variant }, 'youtube');
  if (ready === null && working === null && slot.failedAsset === null && record === null) {
    return null;
  }
  const base = { ...emptyItem(themeId), readyAsset: ready, record };
  const status = record?.status ?? null;

  if (status === 'waiting' && working !== null && ready === null) {
    return {
      ...base,
      state: 'waiting',
      chips: [{ kind: 'rendering', progress: slot.progress }, { kind: 'goesUp' }],
      actions: [],
      progress: slot.progress,
      part: partOf(working),
      waits: false,
    };
  }
  if (status === 'waiting' || status === 'uploading') {
    return {
      ...base,
      state: 'uploading',
      chips: [{ kind: 'uploading' }],
      actions: [{ action: 'status', look: LINK }],
      waits: false,
    };
  }
  if (status === 'scheduled' && record !== null) {
    const actions: YouTubeExtraActionView[] = [];
    if (canChangeScheduledTime(record, input.now)) {
      actions.push({ action: 'changeTime', look: LINK });
    }
    if (record.externalUrl !== null) {
      actions.push({ action: 'open', look: LINK });
    }
    return { ...base, state: 'scheduled', chips: [{ kind: 'publicAt', at: record.publishAt }], actions, waits: false };
  }
  if (status === 'published' && record !== null) {
    const newer = ready !== null && ready.version > record.revision && sourceOfAsset(ready) !== 'youtube';
    const actions: YouTubeExtraActionView[] = newer ? [{ action: 'replace', look: PRIMARY }] : [];
    if (record.externalUrl !== null) {
      actions.push({ action: 'open', look: LINK });
    }
    return {
      ...base,
      state: 'live',
      chips: [{ kind: 'onYouTube' }],
      actions,
      newerRevision: newer && ready !== null ? ready.version : null,
      waits: newer,
    };
  }
  if (status === 'failed' && (record?.failReason === 'reconnect' || input.login === 'reconnect')) {
    return {
      ...base,
      state: 'reconnect',
      chips: [{ kind: 'reconnect' }],
      actions: [{ action: 'reconnect', look: 'button' }],
      waits: true,
    };
  }
  if (status === 'failed' && ready !== null) {
    return {
      ...base,
      state: 'failed',
      chips: [{ kind: 'failed' }],
      actions: [
        { action: 'retry', look: 'button' },
        { action: 'status', look: LINK },
      ],
      waits: true,
    };
  }
  if (status === 'removed' && ready !== null) {
    const chip: YouTubeExtraChip = record?.removedReason === 'private' ? { kind: 'private' } : { kind: 'removed' };
    return {
      ...base,
      state: 'removed',
      chips: [chip],
      actions: [{ action: 'uploadAgain', look: 'button' }],
      waits: true,
    };
  }
  // No live record: the file decides.
  if (ready !== null) {
    if (sourceOfAsset(ready) === 'youtube') {
      return { ...base, state: 'linked', chips: [{ kind: 'onYouTube' }], actions: [], waits: false };
    }
    if (input.login === 'reconnect') {
      return {
        ...base,
        state: 'reconnect',
        chips: [{ kind: 'reconnect' }],
        actions: [{ action: 'reconnect', look: 'button' }],
        waits: true,
      };
    }
    if (input.login === 'none') {
      return { ...base, state: 'readyNotConnected', actions: [{ action: 'connect', look: 'button' }], waits: true };
    }
    return { ...base, state: 'ready', actions: [{ action: 'upload', look: PRIMARY }], waits: true };
  }
  if (working !== null) {
    return {
      ...base,
      state: 'making',
      chips: [{ kind: 'rendering', progress: slot.progress }],
      actions: [],
      progress: slot.progress,
      part: partOf(working),
      waits: false,
    };
  }
  return {
    ...base,
    state: 'renderFailed',
    chips: [{ kind: 'failed' }],
    actions: [{ action: 'makeIt', look: LINK }],
    waits: true,
  };
}

function withoutButtons(item: YouTubeExtraItemView): YouTubeExtraItemView {
  return { ...item, actions: item.actions.filter((one) => one.action === 'open'), waits: false };
}

/** The items of "YouTube extras", in the order of the design. */
export function youtubeExtraItems(input: YouTubeExtrasInput): YouTubeExtraItemView[] {
  const items = YOUTUBE_EXTRA_ORDER.flatMap((themeId) => {
    const slot = input.slots.find((one) => one.slot.themeId === themeId);
    const item = slot === undefined ? null : typeItem(input, slot, themeId);
    return item === null ? [] : [item];
  });
  let shown: YouTubeExtraItemView[];
  if (items.length === 0) {
    // No full video at all (design a1 "noVideo"): the music video item with Make it.
    shown = [
      {
        ...emptyItem('musicVideo'),
        id: 'noVideo',
        state: 'noVideo',
        chips: [{ kind: 'notMade' }],
        actions: [{ action: 'makeIt', look: LINK }],
        waits: false,
      },
    ];
  } else if (input.login === 'none' && items.every((one) => one.record === null && one.state !== 'linked')) {
    // Videos, no YouTube channel and nothing on YouTube (design a1 "notConnected").
    shown = [
      {
        ...emptyItem('musicVideo'),
        id: 'connect',
        themeId: null,
        state: 'notConnected',
        actions: [{ action: 'connect', look: 'button' }],
        waits: true,
      },
    ];
  } else {
    shown = items;
  }
  return input.readOnly ? shown.map(withoutButtons) : shown;
}
